import type { CatalogSetting, ModuleManifest } from '@nexus/core';
import { EmptyState, Page, PageHeader, SettingsSection, SettingsSectionSkeleton } from '@nexus/ui';
import { useQuery } from '@tanstack/react-query';
import { lazy, Suspense } from 'react';
import { useTranslation } from 'react-i18next';
import { adminSettingsQuery } from '../admin/api';
import { CatalogsSection } from '../catalogs/catalogs-section';
import { documentsOfModule } from '../documents/api';
import { useModules } from '../modules/context';
import {
  manageableCatalogs,
  manageableKernelCatalogs,
  sequencesOfModule,
  settingsOfModule,
} from '../modules/settings';
import { useSession } from '../session/session';
import { SequencesSection } from '../settings/sequences-section';
import { AdminSettingRow, PersonalSettingRow, useSaveAdminSetting } from '../settings/setting-rows';
import { questionnairesOfModule } from '../questionnaires/api';
import { QuestionnairesSection } from '../questionnaires/settings-section';

// Document templates load as a chunk of their own, so the settings pages stay small.
const DocumentTemplatesSections = lazy(() =>
  import('../documents/templates-section').then((m) => ({
    default: m.DocumentTemplatesSections,
  })),
);

/**
 * Settings page of a module, rendered by the kernel from the manifest: the personal settings of
 * the module and its submodules, and for people with `<module>.settings.manage` the values of
 * the company, the running numbers, catalogs and the templates of questionnaires and documents.
 * With `company.findings.manage` a module with findings also shows the statuses and priorities
 * of the kernel (docs/architecture/modules.md, Einstellungen).
 */
export function ModuleSettingsPage({ moduleId }: { moduleId: string }) {
  const { t } = useTranslation();
  const session = useSession();
  const manifests = useModules().map((m) => m.manifest);
  const module = manifests.find((m) => m.id === moduleId);
  const { personal, admin } = module
    ? settingsOfModule(module, manifests, session)
    : { personal: [], admin: [] };
  const sequences = module ? sequencesOfModule(module, manifests, session) : [];
  const catalogs = module ? manageableCatalogs(module, manifests, session) : [];
  const kernelCatalogs = module ? manageableKernelCatalogs(module, manifests, session) : [];
  const questionnaires = module ? questionnairesOfModule(module, manifests, session) : [];
  const documents = module ? documentsOfModule(module, manifests, session) : [];
  const values = useQuery({ ...adminSettingsQuery, enabled: admin.length > 0 });
  const save = useSaveAdminSetting();

  if (!module) return null;
  const owners = [module, ...manifests.filter((m) => m.parent === module.id)];
  const ownerName = (owner: ModuleManifest, title: string) =>
    owner.id === module.id ? title : `${t(owner.name)}: ${title}`;

  const adminGroups: { id: string; title: string; settings: CatalogSetting[] }[] = [
    {
      id: 'company',
      title: t('moduleSettings.company'),
      settings: admin.filter((s) => s.level === 'company'),
    },
    {
      id: 'defaults',
      title: t('moduleSettings.personalDefaults'),
      settings: admin.filter((s) => s.level === 'user'),
    },
    {
      id: 'global',
      title: t('moduleSettings.global'),
      settings: admin.filter((s) => s.level === 'global'),
    },
  ].filter((group) => group.settings.length > 0);
  const sections =
    personal.length +
    admin.length +
    sequences.length +
    catalogs.length +
    kernelCatalogs.length +
    questionnaires.length +
    documents.length;

  return (
    <Page>
      <PageHeader
        title={t('moduleSettings.title')}
        description={t('moduleSettings.description')}
        breadcrumbs={[{ label: t(module.name) }, { label: t('moduleSettings.title') }]}
      />
      {sections === 0 ? (
        <EmptyState icon="settings" title={t('moduleSettings.empty')} />
      ) : (
        <div className="flex flex-col gap-6">
          {owners.map((owner) => {
            const own = personal.filter((s) => s.owner === owner.id);
            if (own.length === 0) return null;
            return (
              <SettingsSection
                key={owner.id}
                title={ownerName(owner, t('moduleSettings.personal'))}
              >
                {own.map((setting) => (
                  <PersonalSettingRow key={setting.key} setting={setting} />
                ))}
              </SettingsSection>
            );
          })}
          {adminGroups.length > 0 && !values.data && <SettingsSectionSkeleton rows={3} />}
          {values.data &&
            adminGroups.map((group) => (
              <SettingsSection key={group.id} title={group.title}>
                {group.settings.map((setting) => (
                  <AdminSettingRow
                    key={setting.key}
                    setting={setting}
                    stored={values.data[setting.key]}
                    onChange={(value) => save.mutate({ key: setting.key, value })}
                  />
                ))}
              </SettingsSection>
            ))}
          {sequences.length > 0 && (
            <SequencesSection moduleId={module.id} declarations={sequences} />
          )}
          {kernelCatalogs.length > 0 && <CatalogsSection moduleId={module.id} kernel />}
          {catalogs.length > 0 && <CatalogsSection moduleId={module.id} />}
          {questionnaires.length > 0 && (
            <QuestionnairesSection moduleId={module.id} questionnaires={questionnaires} />
          )}
          {documents.length > 0 && (
            <Suspense fallback={<SettingsSectionSkeleton rows={2} />}>
              <DocumentTemplatesSections moduleId={module.id} documents={documents} />
            </Suspense>
          )}
        </div>
      )}
    </Page>
  );
}
